/*
  Video reviews (snippets/video-reviews.liquid) + compact reviews slider
  (snippets/product-reviews-compact.liquid).
  - [data-pvr] roots: card row + progress bar + fullscreen lightbox.
  - [data-pvr-slider] roots: plain scroll-snap row + progress bar.
  Safe to load several times: each root is set up once (root.__pvr flag).
  No video source is set until a card is tapped; in the player only the active video and its
  neighbours get a src.
*/
(function () {
  if (window.__pvrInit) { window.__pvrInit(); return; }
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function isRtl(el) {
    try { return window.getComputedStyle(el).direction === 'rtl'; } catch (e) { return false; }
  }

  /* Scroll row: lerped progress bar, click-on-bar, smooth mouse drag. Returns { dragged() }.
     - Touch + trackpad/keyboard: native scrolling (native momentum, native overscroll), untouched.
     - Mouse drag: a rAF loop eases the row towards the pointer (current += (target - current) * .18),
       release = momentum from the release velocity, then an ease-out settle onto the nearest snap point.
       Past the first/last card the row stretches with resistance (translate3d on the items) and
       springs back. scroll-snap is off while this runs (no snap fighting = no flicker) and is turned
       back on only once the row rests exactly on a snap point.
     Positions are "logical": 0 = start of the row in reading direction (RTL-safe). */
  var LERP = 0.18, RESIST = 0.35, STRETCH_MAX = 90, SETTLE_MS = 450;
  function easeOut(t) { return 1 - Math.pow(1 - t, 4); } /* close to cubic-bezier(.22,1,.36,1) */

  function slider(track, bar, thumb) {
    var rtl = isRtl(track);
    var items = function () { return track.children; };

    function maxPos() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function getPos() { return Math.abs(track.scrollLeft); }
    function setPos(p) { track.scrollLeft = rtl ? -p : p; }
    function padStart() { var cs = getComputedStyle(track); return parseFloat(rtl ? cs.paddingRight : cs.paddingLeft) || 0; }

    /* ---- progress bar: thumb eases towards the real position ---- */
    var barCur = null, barRaf = 0, thumbW = 0;
    function barTarget(over) {
      var max = maxPos();
      if (max <= 1) return 0;
      return Math.min(Math.max((getPos() + (over || 0) * 0.5) / max, 0), 1);
    }
    function paintBar(f) {
      thumb.style.transform = 'translate3d(' + ((rtl ? -1 : 1) * f * (1 - thumbW) / thumbW * 100) + '%,0,0)';
    }
    function measureBar() {
      if (!bar || !thumb) return;
      var max = maxPos();
      if (max <= 1) { bar.parentNode.style.visibility = 'hidden'; return; }
      bar.parentNode.style.visibility = '';
      thumbW = Math.max(track.clientWidth / track.scrollWidth, 0.08);
      thumb.style.width = (thumbW * 100) + '%';
      bar.classList.add('is-measured'); /* thumb is hidden in CSS until its real size is known (no init jump) */
    }
    function barLoop() {
      barRaf = 0;
      if (!thumb || !thumbW) return;
      var t = barTarget(over);
      if (barCur === null || reduce) barCur = t;
      else barCur += (t - barCur) * 0.25;
      if (Math.abs(t - barCur) < 0.0005) barCur = t;
      paintBar(barCur);
      if (barCur !== t) barRaf = requestAnimationFrame(barLoop);
    }
    function queueBar() { if (!barRaf) barRaf = requestAnimationFrame(barLoop); }
    function onResize() { rtl = isRtl(track); measureBar(); queueBar(); }
    track.addEventListener('scroll', queueBar, { passive: true });
    window.addEventListener('resize', onResize);
    if (window.ResizeObserver) new ResizeObserver(onResize).observe(track);
    measureBar(); barCur = null; barLoop();

    /* ---- snap points (logical positions where a card starts on the content edge) ---- */
    function snapPoints() {
      var tr = track.getBoundingClientRect(), p0 = getPos(), ps = padStart(), max = maxPos(), out = [0, max];
      Array.prototype.forEach.call(items(), function (it) {
        var r = it.getBoundingClientRect();
        var p = (rtl ? tr.right - r.right : r.left - tr.left) + p0 - ps;
        out.push(Math.min(Math.max(p, 0), max));
      });
      return out;
    }
    function nearestSnap(p) {
      var pts = snapPoints(), best = pts[0];
      pts.forEach(function (q) { if (Math.abs(q - p) < Math.abs(best - p)) best = q; });
      return best;
    }

    /* ---- motion state ---- */
    var cur = 0, target = 0, over = 0, loop = 0, anim = null, drag = null, dragged = false, active = false, wheel = null;
    function rubber(x) { /* x = distance past the edge -> visual stretch, with resistance and a soft cap */
      var s = Math.abs(x) * RESIST;
      s = STRETCH_MAX * (1 - Math.exp(-s / STRETCH_MAX));
      return x < 0 ? -s : s;
    }
    function apply(p) {
      var max = maxPos(), c = Math.min(Math.max(p, 0), max);
      over = p - c;                         /* >0 past the end, <0 before the start */
      setPos(c);
      var px = (rtl ? 1 : -1) * over;
      if (Math.abs(px) < 0.05) px = 0;
      track.style.setProperty('--pvr-ox', px.toFixed(2) + 'px');
      track.classList.toggle('is-stretched', px !== 0);
      queueBar();
    }
    function begin() {
      if (active) return;
      active = true;
      cur = target = getPos();
      track.classList.add('is-moving');     /* snap off, will-change on, no hover/transition churn */
    }
    function finish() {
      active = false;
      anim = null;
      apply(Math.min(Math.max(cur, 0), maxPos()));
      track.style.removeProperty('--pvr-ox');
      track.classList.remove('is-moving', 'is-stretched');
      over = 0; queueBar();
    }
    function tick(now) {
      loop = 0;
      if (anim) {
        var t = Math.min((now - anim.t0) / anim.dur, 1);
        cur = anim.from + (anim.to - anim.from) * easeOut(t);
        apply(cur);
        if (t >= 1) { cur = anim.to; finish(); return; }
      } else if (drag || wheel) {
        cur += (target - cur) * LERP;
        if (Math.abs(target - cur) < 0.1) cur = target;
        apply(cur);
      } else return;
      loop = requestAnimationFrame(tick);
    }
    function run() { if (!loop) loop = requestAnimationFrame(tick); }

    /* eased move to a logical position (also used for programmatic moves) */
    function settleTo(to, dur) {
      begin();
      if (reduce) { cur = to; finish(); return; }
      var dist = Math.abs(to - cur);
      if (dist < 0.5 && Math.abs(over) < 0.5) { cur = to; finish(); return; }
      anim = { from: cur, to: to, t0: performance.now(), dur: dur || Math.min(900, Math.max(SETTLE_MS, dist * 0.9)) };
      run();
    }

    if (bar) bar.addEventListener('click', function (e) {
      var r = bar.getBoundingClientRect();
      var f = (e.clientX - r.left) / r.width;
      if (rtl) f = 1 - f;
      settleTo(nearestSnap(f * maxPos()));
    });

    /* ---- mouse drag ---- */
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (anim) { anim = null; cur = getPos() + over; }        /* grab a moving row where it is */
      if (wheel) { clearTimeout(wheel.timer); wheel = null; }
      drag = { x: e.clientX, start: active ? cur : getPos(), samples: [{ x: e.clientX, t: e.timeStamp }] };
      dragged = false;
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!dragged) {
        if (Math.abs(dx) <= 6) return;
        dragged = true;
        begin();
        cur = drag.start;
        track.classList.add('is-dragging');
      }
      var raw = drag.start + (rtl ? dx : -dx), max = maxPos();
      target = raw < 0 ? rubber(raw) : raw > max ? max + rubber(raw - max) : raw;
      if (reduce) { target = Math.min(Math.max(raw, 0), max); cur = target; apply(cur); }
      drag.samples.push({ x: e.clientX, t: e.timeStamp });
      if (drag.samples.length > 6) drag.samples.shift();
      run();
    });
    function release() {
      if (!drag) return;
      var d = drag;
      drag = null;
      track.classList.remove('is-dragging');
      setTimeout(function () { dragged = false; }, 0);
      if (!dragged) return;
      /* release velocity (logical px/ms) from the last ~100ms of samples */
      var s = d.samples, last = s[s.length - 1], first = s[0];
      for (var i = s.length - 1; i >= 0; i--) { if (last.t - s[i].t > 100) break; first = s[i]; }
      var dt = Math.max(last.t - first.t, 1), v = ((rtl ? 1 : -1) * (last.x - first.x)) / dt;
      if (last.t - first.t < 1 || performance.now() - last.t > 120) v = 0; /* held still before release */
      /* momentum with friction .94/frame (~16.7ms): distance = v*16.7 * f/(1-f) */
      var max = maxPos(), from = Math.min(Math.max(cur, -STRETCH_MAX), max + STRETCH_MAX);
      var proj = (cur < 0 || cur > max) ? from : from + v * 16.7 * (0.94 / 0.06);
      cur = from;
      settleTo(nearestSnap(Math.min(Math.max(proj, 0), max)));
    }
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    /* a drag is never a click: swallow the click that follows it (capture, before the card handler) */
    track.addEventListener('click', function (e) { if (dragged) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    /* horizontal wheel / trackpad: native scrolling + mandatory snap fight each other on every tick
       (forward 12px, snap pulls back, forward again = visible jitter). Route it through the same lerp:
       snap off while the events arrive, rubber-band at the edges, one eased settle when they stop.
       Vertical wheel is left alone so the page still scrolls. */
    track.addEventListener('wheel', function (e) {
      var dx = e.deltaX, dy = e.deltaY;
      if (e.shiftKey && !dx) { dx = dy; dy = 0; }
      if (!dx || Math.abs(dx) < Math.abs(dy) || drag) return;
      var max = maxPos();
      if (max <= 1) return;
      e.preventDefault();
      if (e.deltaMode === 1) dx *= 16; else if (e.deltaMode === 2) dx *= track.clientWidth;
      if (!wheel) {
        if (anim) { anim = null; cur = getPos() + over; }
        begin();
        wheel = { raw: active && over ? cur : getPos(), timer: 0 };
      }
      /* deltaX > 0 = content moves towards the end in reading direction (LTR); RTL mirrors */
      wheel.raw += rtl ? -dx : dx;
      wheel.raw = Math.min(Math.max(wheel.raw, -STRETCH_MAX / RESIST * 3), max + STRETCH_MAX / RESIST * 3);
      var raw = wheel.raw;
      target = raw < 0 ? rubber(raw) : raw > max ? max + rubber(raw - max) : raw;
      if (reduce) { target = Math.min(Math.max(raw, 0), max); cur = target; apply(cur); }
      else run();
      clearTimeout(wheel.timer);
      wheel.timer = setTimeout(function () {
        var w = wheel; wheel = null;
        if (!w) return;
        var to = Math.min(Math.max(target, 0), maxPos());
        settleTo(nearestSnap(to));
      }, 140);
    }, { passive: false });
    track.addEventListener('touchstart', function () { if (anim) { cur = getPos(); finish(); } }, { passive: true });
    return { dragged: function () { return dragged; } };
  }

  function setupSlider(root) {
    if (root.__pvr) return;
    var track = root.querySelector('[data-pvr-track]');
    if (!track) return;
    root.__pvr = true;
    slider(track, root.querySelector('[data-pvr-bar]'), root.querySelector('[data-pvr-thumb]'));
  }

  /* ---------- page scroll lock (shared by every instance) ----------
     base.css gives html+body height:100% and body overflow-x:hidden. Putting overflow:hidden on
     <html> stops body's overflow from propagating to the viewport, so body becomes a 100%-tall clip
     box, the document collapses to one screen and scrollY is clamped to 0 (the old "jump to top").
     The lock goes on whichever element feeds the viewport (body while html is overflow:visible),
     so the viewport keeps its scroll offset. The removed scrollbar is replaced by body padding so the
     page content does not shift (scrollbar-gutter would leave a bare strip next to the overlay). */
  var lockState = null;
  function lockScroll() {
    if (lockState) { lockState.n++; return; }
    var de = document.documentElement, b = document.body;
    var target = window.getComputedStyle(de).overflowY === 'visible' ? b : de;
    var sbw = window.innerWidth - de.clientWidth;
    lockState = {
      n: 1, y: window.scrollY || window.pageYOffset || 0, x: window.scrollX || 0, target: target,
      ov: target.style.overflow, gutter: de.style.scrollbarGutter, pr: b.style.paddingRight
    };
    if (sbw > 0) b.style.paddingRight = ((parseFloat(window.getComputedStyle(b).paddingRight) || 0) + sbw) + 'px';
    target.style.overflow = 'hidden';
    de.classList.add('pvr-locked');
  }
  function unlockScroll() {
    if (!lockState || --lockState.n > 0) return;
    var s = lockState, de = document.documentElement;
    lockState = null;
    s.target.style.overflow = s.ov;
    de.style.scrollbarGutter = s.gutter;
    document.body.style.paddingRight = s.pr;
    de.classList.remove('pvr-locked');
    var y = window.scrollY || window.pageYOffset || 0;
    if (Math.abs(y - s.y) > 0.5) {
      try { window.scrollTo({ top: s.y, left: s.x, behavior: 'instant' }); } catch (e) { window.scrollTo(s.x, s.y); }
    }
  }

  var mqDesk = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: false };
  var SVG_PLAY = '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true" focusable="false"><path d="M8 5.5v13l10.5-6.5z"/></svg>';

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }

  function setup(root) {
    if (root.__pvr) return;
    var track = root.querySelector('[data-pvr-track]');
    var modal = root.querySelector('[data-pvr-modal]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pvr-card]'));
    if (!track || !cards.length || !modal) return;
    root.__pvr = true;
    var sl = slider(track, root.querySelector('[data-pvr-bar]'), root.querySelector('[data-pvr-thumb]'));

    /* ---------- player ---------- */
    document.body.appendChild(modal);
    root.__pvrModal = modal;
    var rtl = modal.getAttribute('dir') === 'rtl' || document.body.classList.contains('rtl') || isRtl(root);
    modal.classList.toggle('is-rtl', rtl);
    var feed = modal.querySelector('[data-pvr-feed]');
    var count = modal.querySelector('[data-pvr-count]');
    var muteBtn = modal.querySelector('[data-pvr-mute]');
    var closeBtn = modal.querySelector('.pvr-modal__close');
    var prevBtn = modal.querySelector('[data-pvr-prev]');
    var nextBtn = modal.querySelector('[data-pvr-next]');
    var L = modal.dataset;
    var n = cards.length;
    var slides = [];
    var cur = -1, lastFocus = null, isOpen = false, muted = false, raf = 0, scrollRaf = 0;

    function build() {
      if (slides.length) return;
      cards.forEach(function (c, i) {
        var s = el('div', 'pvr-slide', { role: 'group', 'aria-roledescription': 'slide', 'aria-label': (i + 1) + ' / ' + n });
        var media = el('div', 'pvr-slide__media');
        var img = el('img', 'pvr-slide__poster', { alt: '', decoding: 'async', draggable: 'false' });
        var v = el('video', 'pvr-slide__video', { playsinline: '', 'webkit-playsinline': '', preload: 'none', disablepictureinpicture: '', disableremoteplayback: '', tabindex: '-1' });
        v.playsInline = true;
        var tap = el('button', 'pvr-slide__tap', { type: 'button' });
        var spin = el('span', 'pvr-slide__spin', { 'aria-hidden': 'true' });
        var state = el('span', 'pvr-slide__state', { 'aria-hidden': 'true' });
        state.innerHTML = SVG_PLAY;
        var prog = el('div', 'pvr-slide__progress', { 'aria-hidden': 'true' });
        var fill = el('span', 'pvr-slide__fill');
        prog.appendChild(fill);
        media.appendChild(img); media.appendChild(v); media.appendChild(spin); media.appendChild(tap); media.appendChild(state);
        /* shimmer until the poster is in (theme .skeleton / .is-ready) */
        media.classList.add('skeleton');
        if (c.dataset.poster) {
          var ready = function () { media.classList.add('is-ready'); };
          img.addEventListener('load', ready); img.addEventListener('error', ready);
        } else media.classList.add('is-ready'); media.appendChild(prog);
        if (c.dataset.pUrl) {
          var chip = el('a', 'pvr-slide__chip', { href: c.dataset.pUrl });
          if (c.dataset.pImg) chip.appendChild(el('img', 'pvr-slide__chip-img', { src: c.dataset.pImg, alt: '', width: '44', height: '44', loading: 'lazy' }));
          var body = el('span', 'pvr-slide__chip-body');
          var t = el('span', 'pvr-slide__chip-title'); t.textContent = c.dataset.pTitle || '';
          var pr = el('span', 'pvr-slide__chip-price');
          if (c.dataset.pCompare) { var sEl = el('s'); sEl.textContent = c.dataset.pCompare; pr.appendChild(sEl); pr.appendChild(document.createTextNode(' ')); }
          var pn = el('span'); pn.textContent = c.dataset.pPrice || ''; pr.appendChild(pn);
          body.appendChild(t); body.appendChild(pr); chip.appendChild(body); media.appendChild(chip);
        }
        s.appendChild(media);
        feed.appendChild(s);
        var o = { el: s, v: v, img: img, tap: tap, fill: fill, chip: s.querySelector('.pvr-slide__chip'), src: c.dataset.src, poster: c.dataset.poster };
        slides.push(o);
        tap.addEventListener('click', function () {
          if (i === cur) togglePlay(); else go(i);
        });
        v.addEventListener('playing', function () { s.classList.add('is-started'); s.classList.remove('is-buffering'); });
        v.addEventListener('waiting', function () { if (i === cur) s.classList.add('is-buffering'); });
        v.addEventListener('pause', function () { s.classList.remove('is-buffering'); if (i === cur && isOpen) setPaused(true); });
        v.addEventListener('play', function () { if (i === cur) setPaused(false); });
        v.addEventListener('ended', function () {
          if (i !== cur) return;
          if (mqDesk.matches && cur < n - 1) go(cur + 1);
          else { v.currentTime = 0; playActive(); }
        });
      });
    }

    function setPaused(p) {
      var o = slides[cur];
      if (!o) return;
      o.el.classList.toggle('is-paused', p);
      o.tap.setAttribute('aria-label', p ? L.lPlay : L.lPause);
    }

    function setMuteUi() {
      modal.classList.toggle('is-muted', muted);
      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', muted ? L.lUnmute : L.lMute);
    }

    function playActive() {
      var o = slides[cur];
      if (!o) return;
      var v = o.v;
      v.muted = muted;
      if (v.readyState < 3) o.el.classList.add('is-buffering');
      var p = v.play();
      if (p && p.catch) p.catch(function () {
        if (slides[cur] !== o || !isOpen) return;
        if (!muted) {
          /* autoplay with sound refused: fall back to muted; the mute button turns sound back on */
          muted = true; setMuteUi(); v.muted = true;
          v.play().catch(function () { setPaused(true); });
        } else setPaused(true);
      });
    }

    function togglePlay() {
      var o = slides[cur];
      if (!o) return;
      if (o.v.paused) playActive(); else o.v.pause();
    }

    /* only the active video and its direct neighbours get a src; the rest are released */
    function load() {
      slides.forEach(function (o, j) {
        var d = Math.abs(j - cur);
        if (d <= 2 && o.poster && !o.img.getAttribute('src')) o.img.src = o.poster;
        if (d <= 1) {
          o.v.preload = d === 0 ? 'auto' : 'metadata';
          if (o.v.getAttribute('src') !== o.src) o.v.src = o.src;
        } else if (o.v.hasAttribute('src')) {
          o.v.pause();
          o.v.removeAttribute('src');
          o.v.load();
          o.el.classList.remove('is-started');
        }
      });
    }

    function activate(i) {
      i = Math.max(0, Math.min(n - 1, i));
      if (i === cur) return;
      var old = slides[cur];
      cur = i;
      if (old) {
        old.v.pause();
        try { old.v.currentTime = 0; } catch (e) {}
        old.el.classList.remove('is-paused', 'is-started', 'is-buffering');
        old.fill.style.transform = 'scaleX(0)';
      }
      slides.forEach(function (o, j) {
        var off = j - cur, a = off === 0;
        o.el.style.setProperty('--off', off);
        o.el.classList.toggle('is-active', a);
        o.el.classList.toggle('is-near', Math.abs(off) <= 1);
        o.el.setAttribute('aria-hidden', a ? 'false' : 'true');
        o.tap.tabIndex = a ? 0 : -1;
        o.tap.setAttribute('aria-label', a ? L.lPause : L.lGoto + ' ' + (j + 1));
        if (o.chip) o.chip.tabIndex = a ? 0 : -1;
      });
      count.textContent = (cur + 1) + ' / ' + n;
      prevBtn.disabled = cur === 0;
      nextBtn.disabled = cur === n - 1;
      load();
      playActive();
    }

    /* move to slide i: desktop = transform carousel; mobile = scroll the feed (the scroll handler activates) */
    function go(i) {
      i = Math.max(0, Math.min(n - 1, i));
      if (!mqDesk.matches) {
        var h = feed.clientHeight;
        if (Math.abs(feed.scrollTop - i * h) > 1) {
          feed.scrollTo({ top: i * h, behavior: reduce ? 'auto' : 'smooth' });
          return;
        }
      }
      activate(i);
    }
    function next() { go(cur + 1); }
    function prev() { go(cur - 1); }

    function syncMode() {
      modal.classList.toggle('is-desktop', mqDesk.matches);
      if (!mqDesk.matches && cur > -1) feed.scrollTop = cur * feed.clientHeight;
    }

    feed.addEventListener('scroll', function () {
      if (mqDesk.matches || !isOpen || scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = 0;
        var h = feed.clientHeight;
        if (h && isOpen) activate(Math.round(feed.scrollTop / h));
      });
    }, { passive: true });
    if (mqDesk.addEventListener) mqDesk.addEventListener('change', function () { if (isOpen) syncMode(); });

    function tick() {
      raf = 0;
      if (!isOpen) return;
      var o = slides[cur];
      if (o && o.v.duration) o.fill.style.transform = 'scaleX(' + Math.min(o.v.currentTime / o.v.duration, 1) + ')';
      raf = requestAnimationFrame(tick);
    }

    function open(i) {
      if (isOpen) return;
      lastFocus = cards[i] || document.activeElement;
      build();
      lockScroll();
      modal.hidden = false;
      isOpen = true;
      setMuteUi();
      modal.classList.toggle('is-desktop', mqDesk.matches);
      cur = -1;
      if (!mqDesk.matches) feed.scrollTop = i * feed.clientHeight;
      prevBtn.hidden = nextBtn.hidden = n < 2;
      activate(i);
      if (reduce) modal.classList.add('is-open');
      else requestAnimationFrame(function () { modal.classList.add('is-open'); });
      closeBtn.focus({ preventScroll: true });
      window.addEventListener('keydown', onKey, true);
      if (!raf) raf = requestAnimationFrame(tick);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      window.removeEventListener('keydown', onKey, true);
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      slides.forEach(function (o) {
        o.v.pause();
        if (o.v.hasAttribute('src')) { o.v.removeAttribute('src'); o.v.load(); }
        o.el.classList.remove('is-paused', 'is-started', 'is-active', 'is-near', 'is-buffering');
        o.fill.style.transform = 'scaleX(0)';
      });
      var last = cur;
      cur = -1;
      modal.classList.remove('is-open');
      modal.hidden = true;
      unlockScroll();
      if (last > -1 && cards[last]) {
        /* bring the card into the row horizontally; never scroll the page */
        var c = cards[last], tr = track.getBoundingClientRect(), cr = c.getBoundingClientRect();
        if (cr.left < tr.left || cr.right > tr.right) track.scrollLeft += (cr.left - tr.left);
      }
      var f = (last > -1 && cards[last]) || lastFocus;
      if (f && f.focus) f.focus({ preventScroll: true });
    }

    function focusables() {
      return Array.prototype.filter.call(
        modal.querySelectorAll('button, a[href]'),
        function (x) { return !x.hidden && !x.disabled && x.tabIndex !== -1 && x.offsetParent !== null; }
      );
    }

    function toggleMute() {
      muted = !muted;
      setMuteUi();
      var o = slides[cur];
      if (o) { o.v.muted = muted; if (!muted && o.v.paused) playActive(); }
    }

    function onKey(e) {
      var k = e.key, tag = e.target && e.target.tagName;
      if (k === 'Escape') { e.preventDefault(); close(); return; }
      if (k === 'ArrowRight' || k === 'ArrowLeft') {
        e.preventDefault();
        if ((k === 'ArrowRight') !== rtl) next(); else prev();
        return;
      }
      if (k === 'ArrowDown' || k === 'PageDown') { e.preventDefault(); next(); return; }
      if (k === 'ArrowUp' || k === 'PageUp') { e.preventDefault(); prev(); return; }
      if ((k === ' ' || k === 'k') && tag !== 'BUTTON' && tag !== 'A') { e.preventDefault(); togglePlay(); return; }
      if (k === 'm' || k === 'M') { toggleMute(); return; }
      if (k === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], lastEl = f[f.length - 1], a = document.activeElement;
        if (e.shiftKey && (a === first || !modal.contains(a))) { e.preventDefault(); lastEl.focus({ preventScroll: true }); }
        else if (!e.shiftKey && (a === lastEl || !modal.contains(a))) { e.preventDefault(); first.focus({ preventScroll: true }); }
      }
    }

    cards.forEach(function (c, i) {
      c.addEventListener('click', function (e) {
        e.preventDefault();
        if (sl.dragged()) return;
        open(i);
      });
    });
    modal.querySelectorAll('[data-pvr-close]').forEach(function (b) { b.addEventListener('click', close); });
    /* desktop: a click on the empty stage around the videos counts as a backdrop click */
    feed.addEventListener('click', function (e) { if (e.target === feed && mqDesk.matches) close(); });
    muteBtn.addEventListener('click', toggleMute);
    nextBtn.addEventListener('click', next);
    prevBtn.addEventListener('click', prev);
    /* iOS: stop touch-scroll on the overlay chrome from chaining to the page (the feed scrolls itself) */
    modal.addEventListener('touchmove', function (e) { if (!feed.contains(e.target)) e.preventDefault(); }, { passive: false });

    /* theme editor: close + drop the moved modal when its section is unloaded */
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target && e.target.contains && e.target.contains(track)) { close(); modal.remove(); }
    });
  }

  window.__pvrInit = function () {
    document.querySelectorAll('[data-pvr]').forEach(setup);
    document.querySelectorAll('[data-pvr-slider]').forEach(setupSlider);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', window.__pvrInit);
  else window.__pvrInit();
  document.addEventListener('shopify:section:load', window.__pvrInit);
})();
